import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { 
    Smartphone, Download, Share2, Copy, Check, 
    QrCode, X, Apple, Monitor
} from 'lucide-react';
import { toast } from 'sonner';

export default function PWAInstallModal({ isOpen, onClose }) {
    const { language } = useLanguage();
    const [copied, setCopied] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isInstallable, setIsInstallable] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);
    
    const appUrl = window.location.origin;
    const driverLoginUrl = `${appUrl}/login`;

    useEffect(() => {
        // Check if already installed as PWA
        const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches 
            || window.navigator.standalone 
            || document.referrer.includes('android-app://');
        setIsStandalone(isInStandaloneMode);

        // Check if iOS
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(isIOSDevice);

        // Listen for beforeinstallprompt event
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setIsInstallable(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) {
            toast.error(language === 'el' 
                ? 'Η εγκατάσταση δεν είναι διαθέσιμη αυτή τη στιγμή' 
                : 'Installation not available at this time'
            );
            return;
        }

        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
            toast.success(language === 'el' 
                ? 'Η εφαρμογή εγκαταστάθηκε επιτυχώς!' 
                : 'App installed successfully!'
            );
            setDeferredPrompt(null);
            setIsInstallable(false);
        }
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(driverLoginUrl);
            setCopied(true);
            toast.success(language === 'el' ? 'Αντιγράφηκε!' : 'Copied!');
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast.error(language === 'el' ? 'Αποτυχία αντιγραφής' : 'Failed to copy');
        }
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Cofleet Driver App',
                    text: language === 'el' 
                        ? 'Σαρώστε για να εγκαταστήσετε την εφαρμογή Cofleet' 
                        : 'Scan to install the Cofleet app',
                    url: driverLoginUrl
                });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    toast.error(language === 'el' ? 'Αποτυχία κοινοποίησης' : 'Failed to share');
                }
            }
        } else {
            handleCopyLink();
        }
    };

    const handleDownloadQR = () => {
        const svg = document.getElementById('cofleet-qr-code');
        if (!svg) return;

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
            canvas.width = 400;
            canvas.height = 400;
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, 400, 400);
            
            const link = document.createElement('a');
            link.download = 'cofleet-qr-code.png';
            link.href = canvas.toDataURL('image/png');
            link.click();
            
            toast.success(language === 'el' ? 'QR Code κατεβάστηκε!' : 'QR Code downloaded!');
        };
        
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md bg-white p-6">
                <DialogHeader className="pb-4">
                    <DialogTitle className="flex items-center gap-2 text-slate-800">
                        <Smartphone className="w-5 h-5 text-[#1B7EB5]" />
                        {language === 'el' ? 'Εγκατάσταση Εφαρμογής' : 'Install App'}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-5">
                    {/* QR Code Section */}
                    <div className="flex flex-col items-center p-5 bg-slate-50 rounded-xl">
                        <div className="bg-white p-3 rounded-xl shadow-sm mb-3">
                            <QRCodeSVG
                                id="cofleet-qr-code"
                                value={driverLoginUrl}
                                size={180}
                                level="H"
                                includeMargin={true}
                                fgColor="#1B7EB5"
                                bgColor="#ffffff"
                            />
                        </div>
                        <p className="text-sm text-slate-600 text-center mb-2">
                            {language === 'el' 
                                ? 'Σαρώστε με την κάμερα του κινητού σας' 
                                : 'Scan with your phone camera'
                            }
                        </p>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDownloadQR}
                            className="gap-2"
                        >
                            <Download className="w-4 h-4" />
                            {language === 'el' ? 'Λήψη QR Code' : 'Download QR Code'}
                        </Button>
                    </div>

                    {/* Install Instructions */}
                    <div className="space-y-3">
                        <h4 className="font-medium text-slate-800 flex items-center gap-2 text-sm">
                            <QrCode className="w-4 h-4 text-[#1B7EB5]" />
                            {language === 'el' ? 'Οδηγίες Εγκατάστασης' : 'Installation Instructions'}
                        </h4>

                        {isStandalone ? (
                            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                <p className="text-green-700 text-sm flex items-center gap-2">
                                    <Check className="w-4 h-4" />
                                    {language === 'el' 
                                        ? 'Η εφαρμογή είναι ήδη εγκατεστημένη!' 
                                        : 'App is already installed!'
                                    }
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Direct Install Button (Chrome/Edge) */}
                                {isInstallable && (
                                    <Button
                                        onClick={handleInstallClick}
                                        className="w-full bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 gap-2"
                                    >
                                        <Download className="w-4 h-4" />
                                        {language === 'el' ? 'Εγκατάσταση Τώρα' : 'Install Now'}
                                    </Button>
                                )}

                                {/* iOS Instructions */}
                                {isIOS && (
                                    <div className="p-3 bg-slate-100 rounded-lg space-y-2">
                                        <div className="flex items-center gap-2 text-slate-700 font-medium text-sm">
                                            <Apple className="w-4 h-4" />
                                            iPhone / iPad
                                        </div>
                                        <ol className="text-xs text-slate-600 space-y-1 ml-6 list-decimal">
                                            <li>{language === 'el' ? 'Ανοίξτε στο Safari' : 'Open in Safari'}</li>
                                            <li>{language === 'el' ? 'Πατήστε το κουμπί Share' : 'Tap the Share button'} <Share2 className="w-3 h-3 inline" /></li>
                                            <li>{language === 'el' ? 'Επιλέξτε "Add to Home Screen"' : 'Select "Add to Home Screen"'}</li>
                                        </ol>
                                    </div>
                                )}

                                {/* Android Instructions */}
                                {!isIOS && !isInstallable && (
                                    <div className="p-3 bg-slate-100 rounded-lg space-y-2">
                                        <div className="flex items-center gap-2 text-slate-700 font-medium text-sm">
                                            <Monitor className="w-4 h-4" />
                                            Android / Desktop
                                        </div>
                                        <ol className="text-xs text-slate-600 space-y-1 ml-6 list-decimal">
                                            <li>{language === 'el' ? 'Ανοίξτε στο Chrome' : 'Open in Chrome'}</li>
                                            <li>{language === 'el' ? 'Πατήστε το μενού (⋮)' : 'Tap the menu (⋮)'}</li>
                                            <li>{language === 'el' ? 'Επιλέξτε "Install app" ή "Add to Home screen"' : 'Select "Install app" or "Add to Home screen"'}</li>
                                        </ol>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Share Link Section */}
                    <div className="pt-3 border-t border-slate-200">
                        <p className="text-xs text-slate-500 mb-2">
                            {language === 'el' ? 'Ή μοιραστείτε τον σύνδεσμο:' : 'Or share the link:'}
                        </p>
                        <div className="flex gap-2">
                            <div className="flex-1 px-3 py-2 bg-slate-100 rounded-lg text-xs text-slate-600 truncate">
                                {driverLoginUrl}
                            </div>
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={handleCopyLink}
                                className="shrink-0 h-8 w-8"
                            >
                                {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                            </Button>
                            {navigator.share && (
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={handleShare}
                                    className="shrink-0 h-8 w-8"
                                >
                                    <Share2 className="w-3 h-3" />
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
